import { Link, useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Block, EntryHeader, EntryTabs, Panel, RunTab, PromptsTab, MyRunsTab, useEntryTab } from "@/components/run/EntryLayout";
import { EntityAvatar } from "@/components/catalogue/EntityAvatar";
import { Needs } from "@/pages/AgentPage";
import { NotFoundEntry } from "@/pages/TeamPage";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { agentByWorkingName, agentName } from "@/lib/catalogue";

export default function WorkflowPage() {
  const { wid } = useParams();
  const { user } = useAuth();
  const { workflowsByKey, agents, loading } = useCatalogue();
  const wf = workflowsByKey[wid];
  const { tab, setTab, tabs } = useEntryTab(!!wf?.live, !!wf);
  if (loading) return <Skeleton className="h-64 rounded-xl" />;
  if (!wf) return <NotFoundEntry label="workflow" testid="workflow-not-found" />;
  const gates = wf.human_gates || [];
  const canRun = wf.live && (user.role !== "viewer" || wf.viewer_can_run);
  return (
    <div data-testid="workflow-page">
      <EntryHeader entity={wf} id={wf.id} title={wf.name} kindLabel={`Workflow${wf.trigger ? `, starts from ${wf.trigger}` : ""}`} status={wf.live ? "live" : "soon"} canRun={canRun} onRun={() => setTab("run")} />
      <EntryTabs tabs={tabs} tab={tab} setTab={setTab} />
      <Panel id="run" tab={tab}><RunTab kind="workflow" entity={wf} id={wf.id} gates={gates} /></Panel>
      <Panel id="about" tab={tab}>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {wf.key_benefit && <p className="text-base font-medium" data-testid="workflow-key-benefit">{wf.key_benefit}</p>}
            <Block title="Use me for" testid="workflow-use-me-for">{wf.use_me_for || wf.one_line || "Not described yet."}</Block>
            <Block title="What you get" testid="workflow-what-you-get">{wf.what_you_get || "Not described yet."}</Block>
            {(wf.bio || wf.description) && <Block title="About" testid="workflow-about">{wf.bio || wf.description}</Block>}
          </div>
          <div className="space-y-4">
            <Block title="Steps" testid="workflow-steps">
              {gates.length === 0 ? "This workflow runs without approval pauses." : (
                <><p>This workflow pauses for your approval at these gates.</p><ol className="mt-3 list-decimal space-y-1 pl-5 text-foreground">{gates.map((g) => <li key={g}>{g}</li>)}</ol></>
              )}
            </Block>
            <Block title="Agents involved" testid="workflow-agents">
              <ul className="flex flex-wrap gap-2">
                {(wf.agents || []).map((n) => {
                  const a = agentByWorkingName(n, agents);
                  return <li key={n}>{a ? <Link to={`/agents/${a.emp_no}`} className="inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-foreground transition-quick hover:bg-muted" data-testid={`workflow-agent-${a.emp_no}`}><EntityAvatar entity={a} name={agentName(a)} size="xs" />{agentName(a)}</Link> : <span className="inline-block rounded-full border border-dashed px-3 py-1">{n}</span>}</li>;
                })}
              </ul>
            </Block>
          </div>
        </div>
      </Panel>
      <Panel id="needs" tab={tab}><Needs inputs={wf.inputs_summary || []} fields={wf.trigger_fields || []} fieldsLabel="Trigger fields" testid="workflow-what-it-needs" /></Panel>
      <Panel id="prompts" tab={tab}><PromptsTab id={wf.id} /></Panel>
      <Panel id="runs" tab={tab}><MyRunsTab id={wf.id} tab={tab} /></Panel>
    </div>
  );
}
